import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "../lib/cn";

export function PanelHeader({ title, description, action, className }: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return <div className={cn("flex items-start justify-between gap-3", className)}>
    <div className="min-w-0">
      <h2 className="text-sm font-semibold text-primary">{title}</h2>
      {description && <p className="mt-1 text-xs leading-5 text-muted">{description}</p>}
    </div>
    {action}
  </div>;
}

export function PanelBody({ children, className, ...props }: {
  children: ReactNode;
  className?: string;
  "aria-label"?: string;
}) {
  return <div {...props} className={cn("min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5", className)}>{children}</div>;
}

export function PanelGroup({ title, description, children, className, variant = "plain" }: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  variant?: "plain" | "card";
}) {
  return <section className={cn("space-y-4", variant === "card" ? "rounded-xl border border-border bg-surface p-4" : "border-t border-border pt-5 first:border-t-0 first:pt-0", className)}>
    <div>
      <h3 className="text-sm font-semibold text-primary">{title}</h3>
      {description && <p className="mt-1 text-xs leading-5 text-muted">{description}</p>}
    </div>
    {children}
  </section>;
}

export function PanelDisclosure({ title, description, children, className, defaultOpen = false }: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  defaultOpen?: boolean;
}) {
  // One line when closed, like a group heading: the title, then a muted hint on the same line.
  return <details open={defaultOpen} className={cn("group border-t border-border pt-5", className)}>
    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-sm text-sm font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus [&::-webkit-details-marker]:hidden">
      <span className="flex min-w-0 items-baseline gap-2">{title}{description && <span className="min-w-0 truncate text-xs font-normal text-muted">{description}</span>}</span><ChevronDown className="size-4 shrink-0 text-muted transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden />
    </summary>
    <div className="mt-4 space-y-4">{children}</div>
  </details>;
}
